import { useCallback, useEffect, useRef, type ComponentRef } from 'react'
import { cn, Switch, TextEditor } from '@quickmd-llc/design-system'
import '@quickmd-llc/design-system/components/text-editor/TextEditor.css'
import { stripHtml } from '@/pages/edit-state-settings/helper'
import { text } from '@/pages/edit-state-settings/constants'

interface EditStateSettingsBodyViewProps {
  onChangePhoneRule: () => void
  onChangeRequiresDrugScreening: () => void
  isPhoneVisitAllowed: boolean | undefined
  requiresDrugScreening: boolean | null | undefined
  onSetDrugScreenRules: (value: string) => void
  drugScreeningText: string | null | undefined
  drugError: string
}

const EditStateSettingsBodyView = ({
  onChangePhoneRule,
  onChangeRequiresDrugScreening,
  isPhoneVisitAllowed,
  requiresDrugScreening,
  onSetDrugScreenRules,
  drugScreeningText,
  drugError,
}: EditStateSettingsBodyViewProps) => {
  // The DS TextEditor is uncontrolled — its value is seeded imperatively through the ref.
  // `lastEmittedRef` records the HTML the editor last emitted so the seed effect can tell an
  // *external* value change (initial load, drug-screening toggle restore, back-nav) from the
  // user's own keystrokes and only re-seed on the former — re-seeding on every keystroke would
  // reset the caret.
  const editorRef = useRef<ComponentRef<typeof TextEditor>>(null)
  const lastEmittedRef = useRef<string | null | undefined>(undefined)

  useEffect(() => {
    const next = drugScreeningText ?? ''
    // Compare by visible text: the callback normalises an empty editor ('<p><br></p>') down to '',
    // so an exact compare would loop and re-seed (clearing the caret) on that normalisation.
    if (stripHtml(next) !== stripHtml(lastEmittedRef.current ?? '')) {
      lastEmittedRef.current = next
      editorRef.current?.onSetValue(next)
    }
  }, [drugScreeningText])

  const handleChangeDrugScreenRules = useCallback(
    (html: string) => {
      lastEmittedRef.current = html
      onSetDrugScreenRules(html)
    },
    [onSetDrugScreenRules],
  )

  return (
    /* ess-card: bg-white, border 1px solid shade-10, border-radius 8px (large), overflow hidden */
    <div className='overflow-hidden rounded-large border border-shade-10 bg-white'>
      {/* ess-card-header: padding 20px 24px */}
      <div className='px-sp-3 py-sp-2.5'>
        {/* ess-card-title: text-size-4 (16px), font-weight 600, line-height 24px, text-text-dark */}
        <span className='text-size-4 font-semibold leading-6 text-text-dark'>State information</span>
      </div>
      {/* ess-card-divider: height 1px, bg shade-10 */}
      <div className='h-[1px] bg-shade-10' />
      {/* ess-card-body: padding 24px, flex-col, gap 24px */}
      <div className='flex flex-col gap-sp-3 p-sp-3'>
        {/* ess-toggles-grid: grid, 2 cols, gap 24px */}
        <div className='grid grid-cols-2 gap-sp-3'>
          {/* ess-toggle-section: flex-col, gap 12px */}
          <div className='flex flex-col gap-sp-1.5'>
            {/* ess-toggle-label: text-size-3 (14px), font-weight 600, line-height 20px, text-text-dark */}
            <span className='text-size-3 font-semibold leading-5 text-text-dark'>Phone visits</span>
            {/* ess-toggle-row: flex, align-items center, gap 12px */}
            <div className='flex items-center gap-sp-1.5'>
              <Switch checked={isPhoneVisitAllowed ?? false} onChange={onChangePhoneRule} size='m' />
              {/* ess-toggle-value: text-size-3 (14px), font-weight 400, line-height 20px, text-text-light (rgba(37,56,69,0.7) = #253845B2 = text-text-light) */}
              <span className='text-size-3 font-normal leading-5 text-text-light'>
                {isPhoneVisitAllowed ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
          <div className='flex flex-col gap-sp-1.5'>
            <span className='text-size-3 font-semibold leading-5 text-text-dark'>Drug screening</span>
            <div className='flex items-center gap-sp-1.5'>
              <Switch checked={requiresDrugScreening ?? false} onChange={onChangeRequiresDrugScreening} size='m' />
              <span className='text-size-3 font-normal leading-5 text-text-light'>
                {requiresDrugScreening ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        </div>
        {requiresDrugScreening ?
          <div className='flex flex-col gap-sp-0.5'>
            {/* ess-drug-rules-label: text-size-3 (14px), font-weight 600, line-height 20px, text-text-dark */}
            <span className='text-size-3 font-semibold leading-5 text-text-dark'>Drug screen rules*</span>
            {/* DS rich-text editor (enableHTML) — restores the formatting toolbar (B/I/U, strikethrough,
                lists, headings, link) the plain textarea dropped. Stores HTML in DrugScreenHtml. The
                standard toolbar renders pinned at the top of the edit box. isError + descriptionLocalError
                drive the required-field styling/message (no separate error span needed). */}
            <TextEditor
              ref={editorRef}
              enableHTML
              setText={handleChangeDrugScreenRules}
              placeHolderText={text.PLACEHOLDER}
              isError={!!drugError}
              descriptionLocalError={drugError}
              id='drugScreenRulesEditor'
              // Lock the input area to a fixed 200px (no DS token — arbitrary). Fixed height +
              // overflow-y-auto so it no longer grows as the user presses Enter; content beyond
              // 200px scrolls inside the editor instead. The .ds-quill-toolbar mt/mb overrides
              // tighten the gap above (mt-1 = 4px) and set 16px (mb-sp-2) below the toolbar,
              // between it and the text box. self-start left-aligns the toolbar pill, which the DS
              // ships right-aligned (align-self: self-end). !important overrides Quill's own styles.
              containerAdditionalClassName={cn(
                '[&_.ds-quill-toolbar]:!mb-sp-2 [&_.ds-quill-toolbar]:!mt-1 [&_.ds-quill-toolbar]:!self-start [&_.ql-editor]:!h-[200px] [&_.ql-editor]:!overflow-y-auto',
              )}
            />
          </div>
        : null}
      </div>
    </div>
  )
}

export default EditStateSettingsBodyView
